import React, { useState, useEffect, useCallback } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

const AssignStudyMemberModal = ({ studyId, projectId, existingAssignments, onClose, onSuccess }) => {
    const [members, setMembers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [role, setRole] = useState("reviewer");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingMembers, setLoadingMembers] = useState(true);

    const fetchProjectMembers = useCallback(async () => {
        try {
            setLoadingMembers(true);
            const response = await API.get(`/projects/${projectId}/members/`);

            // Get list of already assigned user IDs
            const assignedUserIds = new Set(
                existingAssignments?.map(a => a.user?.id) || []
            );

            // Combine owner and members, excluding already assigned users
            // Include both accepted AND pending members so they can be assigned immediately after invitation
            const allMembers = [];

            if (response.data.owner && !assignedUserIds.has(response.data.owner.user.id)) {
                allMembers.push({
                    ...response.data.owner.user,
                    projectRole: "owner",
                    membershipStatus: "accepted"
                });
            }

            response.data.members
                .filter(m => (m.status === "accepted" || m.status === "pending") && !assignedUserIds.has(m.user.id))
                .forEach(m => {
                    allMembers.push({
                        ...m.user,
                        projectRole: m.role,
                        membershipStatus: m.status
                    });
                });

            setMembers(allMembers);
        } catch (err) {
            console.error("Error fetching project members:", err);
            toast.error("Failed to fetch project members.");
        } finally {
            setLoadingMembers(false);
        }
    }, [projectId, existingAssignments]);

    useEffect(() => {
        fetchProjectMembers();
    }, [fetchProjectMembers]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedUserId) {
            toast.error("Please select a member to assign.");
            return;
        }

        setLoading(true);
        try {
            await API.post(`/studies/${studyId}/assign/`, {
                user_id: parseInt(selectedUserId),
                role: role,
                notes: notes,
            });
            toast.success("Member assigned successfully!");
            onSuccess();
        } catch (err) {
            console.error("Error assigning member:", err);
            const message = err.response?.data?.error || "Failed to assign member.";
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Assign Member to Study</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={loading}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {loadingMembers ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : members.length === 0 ? (
                                <div className="alert alert-info">
                                    All project members are already assigned to this study.
                                </div>
                            ) : (
                                <>
                                    <div className="mb-3">
                                        <label className="form-label">Select Member *</label>
                                        <select
                                            className="form-select"
                                            value={selectedUserId}
                                            onChange={(e) => setSelectedUserId(e.target.value)}
                                            required
                                        >
                                            <option value="">Choose a member...</option>
                                            {members.map((member) => (
                                                <option key={member.id} value={member.id}>
                                                    {member.username}
                                                    {member.first_name && ` (${member.first_name} ${member.last_name})`}
                                                    {` - ${member.projectRole}`}
                                                    {member.membershipStatus === "pending" && " (pending invitation)"}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="text-muted">
                                            Members with pending invitations can be assigned immediately.
                                        </small>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Assignment Role</label>
                                        <select
                                            className="form-select"
                                            value={role}
                                            onChange={(e) => setRole(e.target.value)}
                                        >
                                            <option value="reviewer">Reviewer</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Notes (Optional)</label>
                                        <textarea
                                            className="form-control"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="Add any notes about this assignment..."
                                            rows="2"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading || loadingMembers || members.length === 0}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Assigning...
                                    </>
                                ) : (
                                    "Assign Member"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AssignStudyMemberModal;
